// mKonnekt (Dezi) tobacco-compliance status reporting.
//
//   POST {BASE_NODE_URL}Mkonnekt_discount/update_order_status
//
// WHY THIS EXISTS
// Only orders sitting at COMPLETED are included in the SkanData files mKonnekt generates for the
// manufacturers. A sale that is later cancelled or refunded therefore has to be re-reported, or it
// stays counted as manufacturer-funded tobacco volume — a compliance discrepancy, not merely stale
// data.
//
// STATUS RULES (confirmed by mKonnekt)
// mKonnekt accepts exactly two statuses: COMPLETED (001) and REFUNDED (007).
//   - Full refund     -> REFUNDED
//   - Void / cancel   -> REFUNDED
//   - Partial refund  -> COMPLETED, resent with the kept items/totals only. The backend rebuilds the
//                        ticket from the DB using the kept quantities (and sends REFUNDED itself if
//                        no tobacco lines remain).
//
// WHAT WE SEND
// Only merchant_id, orderId, status (+ optional description). `status` is ADVISORY: the Node backend
// decides what to report from the database (m_status, kept items) and applies the reporting rules
// above itself, so nothing about amounts, dates, line items or the original discount quote belongs
// in this call. The `rcn` is deliberately OMITTED — the backend resolves the store's own registered
// RCN (and ignores a body rcn anyway).
//
// WHAT COMES BACK
// HTTP 200 { status: "success", message, result: { action, reason?, reported_status? } } where
// action is "sent" | "skipped" | "queued" | "in_progress". EVERY action is success: "skipped" (e.g.
// never_reported, nothing_to_report, unchanged, week_generated) is the server correctly deciding
// there is nothing to send, not an error. Failures are { status: "failed", message } with HTTP
// 400/403/404/409/502. A background sweep on the server also reports every order with a UPC line, so
// this call only makes the report prompt — a miss here is caught up later.
//
// AUTH
// The route requires the admin-panel session: `Authorization: Bearer <token>` header AND body
// `merchant_id`, `token_id`, `login_type`. A 401 is a verdict, not a transport problem — no retry.
//
// ORDERING — LOAD-BEARING
// Call this only AFTER the local refund/cancel has been committed server-side. The backend rebuilds
// the payload from whatever the order tables say at the moment it runs, so firing early reports the
// pre-refund state.
//
// CONTRACT: never throws, never blocks. A compliance report that did not land is an ops problem; it
// must never surface to a cashier mid-transaction or fail a refund that already moved money.

import axios from "axios";
import Config from "../../Constants/Config";
import { NODE_BASE, authFields, authHeaders } from "./nodeAuth";

/** The only two statuses mKonnekt accepts. */
export const MKONNEKT_ORDER_STATUS = Object.freeze({
  COMPLETED: "COMPLETED",
  REFUNDED: "REFUNDED",
});

const VALID_STATUSES = Object.values(MKONNEKT_ORDER_STATUS);

// The backend retries mKonnekt up to 3 times with backoff, so one request can legitimately stay open
// for ~31s. A shorter timeout aborts while the server is still working and — because the upstream
// call may already have succeeded — reports a failure that did not happen.
const REQUEST_TIMEOUT_MS = 35000;

const RETRY_DELAYS_MS = [2000, 6000];

/**
 * Merchants the backend has told us are not provisioned for Dezi (403).
 *
 * Most stores are not tobacco stores, and every one of their cancellations would otherwise fire a
 * pointless round trip that can only come back 403. The verdict is a per-merchant property that does
 * not change inside a session, so one answer is enough. Deliberately in memory only — a merchant who
 * gets provisioned mid-session is picked up on the next page load rather than being cached out of
 * the feature indefinitely.
 */
const notProvisioned = new Set();

/**
 * (orderId, status) pairs already reported (or in flight) this session — guards double-submits and
 * StrictMode. REFUNDED is terminal, so it stays claimed. COMPLETED is a partial-refund resend and a
 * later partial refund on the same order must be reported again, so it is only claimed while the
 * request is in flight.
 */
const reported = new Set();

const wait = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Network failures and 5xx (incl. 502 — mKonnekt unreachable) are transient. The backend's own
 * verdicts are not: 400 (bad request), 401 (auth), 403 (store not provisioned), 404 (no such order for
 * this merchant) and 409 (conflicting state) return the same answer no matter how often they are asked.
 */
const isRetryable = (error) => {
  const status = error?.response?.status;
  if (status === undefined) return true; // network error / timeout
  return status >= 500;
};

async function postStatus({ merchant, order, nextStatus, description, key }) {
  const { token, token_id, login_type } = authFields();
  const url = NODE_BASE + Config.MKONNEKT_UPDATE_ORDER_STATUS;
  const body = {
    merchant_id: merchant,
    orderId: order,
    status: nextStatus,
    token_id,
    login_type,
  };
  if (description != null && String(description).trim() !== "") {
    body.description = String(description).trim();
  }

  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      const res = await axios.post(url, body, {
        headers: authHeaders(token),
        timeout: REQUEST_TIMEOUT_MS,
      });
      if (res?.data?.status === "success") {
        // Any action — sent, skipped, queued, in_progress — is the server's accepted decision.
        const result = res.data.result || {};
        return {
          ok: true,
          action: String(result.action || ""),
          reason: result.reason ? String(result.reason) : undefined,
          reportedStatus: result.reported_status ? String(result.reported_status) : undefined,
          data: res.data,
        };
      }
      // A 200 carrying a non-success body is mKonnekt's own verdict, not a transport problem —
      // asking again just repeats it.
      console.error(
        "mKonnekt update_order_status rejected:",
        res?.data?.message || "unknown reason"
      );
      return { ok: false, data: res?.data };
    } catch (error) {
      const httpStatus = error?.response?.status;
      if (httpStatus === 403) {
        // Store has no enabled RCN — it is not a Dezi store, so there was never anything to
        // report. Remember it and stay quiet; this is an expected answer, not a failure.
        notProvisioned.add(merchant);
        return { ok: false, skipped: "not-a-dezi-store" };
      }
      if (httpStatus === 401) {
        // Session rejected. Asking again with the same credentials repeats the answer, but release
        // the claim so the report can be attempted again once the user has a valid session.
        reported.delete(key);
        console.error(
          "mKonnekt update_order_status unauthorized:",
          error?.response?.data?.message || error?.message || error
        );
        return { ok: false, error };
      }
      if (attempt < RETRY_DELAYS_MS.length && isRetryable(error)) {
        await wait(RETRY_DELAYS_MS[attempt]);
        continue;
      }
      // Let a transient failure be retried by a later attempt on the same order rather than being
      // permanently suppressed by the de-dup guard.
      if (isRetryable(error)) reported.delete(key);
      console.error(
        "mKonnekt update_order_status failed:",
        error?.response?.data?.message || error?.message || error
      );
      return { ok: false, error };
    }
  }

  return { ok: false };
}

/**
 * Report a refund, void/cancel (REFUNDED) or partial refund (COMPLETED) to mKonnekt.
 *
 * @param {Object} args
 * @param {string} args.merchantId   QuickVee merchant id
 * @param {string} args.orderId      the order id reported at sale time
 * @param {string} args.status       one of MKONNEKT_ORDER_STATUS
 * @param {string} [args.description] free text (refund reason, who cancelled) — not validated
 * @returns {Promise<{ok: boolean, action?: string, reason?: string, reportedStatus?: string,
 *   skipped?: string, data?: Object, error?: any}>} never rejects. `ok` with `action: "skipped"` is
 *   the server deciding there was nothing to send — not a failure. (`skipped` without `ok` is a
 *   client-side short-circuit: no request was made.)
 */
export async function reportMkonnektOrderStatus({
  merchantId,
  orderId,
  status,
  description,
} = {}) {
  const merchant = String(merchantId ?? "").trim();
  const order = String(orderId ?? "").trim();
  const nextStatus = String(status ?? "").trim();

  if (!NODE_BASE) return { ok: false, skipped: "no-node-base" };
  if (!merchant || !order) return { ok: false, skipped: "incomplete-order" };
  if (!VALID_STATUSES.includes(nextStatus)) return { ok: false, skipped: "invalid-status" };
  if (notProvisioned.has(merchant)) return { ok: false, skipped: "not-a-dezi-store" };

  const key = `${merchant}::${order}::${nextStatus}`;
  if (reported.has(key)) return { ok: false, skipped: "already-reported" };
  // Claim it before awaiting so a second call cannot slip through while this one is in flight.
  reported.add(key);

  const result = await postStatus({ merchant, order, nextStatus, description, key });
  // A partial-refund resend is repeatable: release the claim once this attempt has finished.
  if (nextStatus === MKONNEKT_ORDER_STATUS.COMPLETED) reported.delete(key);
  return result;
}

/** Test seam — production code never needs to forget a report or a provisioning verdict. */
export function resetMkonnektOrderStatusReports() {
  reported.clear();
  notProvisioned.clear();
}

export default reportMkonnektOrderStatus;
